import type { BatteryArbitragePresentationDto } from "~/api/dto/battery-arbitrage.dto"

type Assumptions = BatteryArbitragePresentationDto[`assumptions`]

type Estimate = Pick<
  BatteryArbitragePresentationDto,
  `estimateBasis` | `modeledBillingScope`
> & {
  assumptions?: { measurementBasis?: string }
}

export const arbitrageBasisQualifier = (estimate: Estimate): string =>
  estimate.estimateBasis === `personalized`
    ? `Personalized estimate. Based on measured interval data`
    : `Generalized estimate. Modeled scenario`

export const arbitrageBillLabel = (estimate: Estimate): string => {
  if (estimate.modeledBillingScope === `grid_purchases_only`) {
    return `Grid-purchase scenario`
  }
  return estimate.estimateBasis === `personalized`
    ? `Estimated utility bill`
    : `Scenario utility bill`
}

export const arbitrageScopeExplanation = (estimate: Estimate): string => {
  if (estimate.modeledBillingScope === `grid_purchases_only`) {
    return `Only modeled grid purchases are compared. These amounts do not reproduce the home's complete bill or net metering credits.`
  }
  if (estimate.estimateBasis === `personalized`) {
    return estimate.assumptions?.measurementBasis === `GRID_IMPORT`
      ? `The calculation estimates the complete utility bill using measured grid imports, not assumed household usage.`
      : `The calculation estimates the complete utility bill using the home's measured usage.`
  }
  return `These modeled scenario bills are based on representative usage or rate assumptions, not matched customer bills.`
}

export const arbitrageLoadShapeBasis = (assumptions: Assumptions): string =>
  assumptions.loadBasis

/**
 * A stored percentage as text. Reserve and escalation are floats, so a saved
 * 12.5 can arrive as 12.500000000000002; two decimals is finer than any
 * assumption a seller can enter and keeps that noise off the page.
 */
export const formatArbitragePercent = (value: number): string =>
  Number.isFinite(value) ? `${Number(value.toFixed(2))}%` : `an unreadable value`

/**
 * What the calculation assumed, in the words a homeowner reads, so the figures
 * beside it can be interpreted. Every value comes from the saved run.
 *
 * Degradation is stated as the run recorded it rather than as the fixed 2%
 * product decision, so a run calculated under a different rate cannot be
 * described with today's.
 */
export const arbitrageAssumptions = (
  assumptions: Assumptions,
): Array<{ label: string; value: string }> => [
  {
    label: `Usage behind the estimate`,
    value: arbitrageLoadShapeBasis(assumptions),
  },
  {
    label: `Rate basis`,
    value: assumptions.rateBasis,
  },
  ...(assumptions.unchangedExports
    ? [
        {
          label: `Existing solar exports`,
          value: `Existing exports and their billing treatment are held unchanged before and after battery dispatch.`,
        },
      ]
    : []),
  {
    label: `Backup reserve held for outages`,
    value: `${formatArbitragePercent(
      assumptions.backupReservePct,
    )} of usable capacity`,
  },
  {
    label: `Annual utility-rate change`,
    value: `${formatArbitragePercent(
      assumptions.utilityRateEscalationPct,
    )} a year`,
  },
  {
    label: `Battery benefit decline`,
    value: `${formatArbitragePercent(assumptions.degradationRate * 100)} a year`,
  },
]
